import { EditOutlined } from '@ant-design/icons'
import { IEvent, IPayloadUpdateEvent, TypeEventEnum } from 'app/api/event/model'
import R from 'app/assets/R'
import { PermissionEnum } from 'app/common/enum'
import { BaseButton } from 'app/components/common/BaseButton'
import { BaseCol } from 'app/components/common/BaseCol'
import { BaseRow } from 'app/components/common/BaseRow'
import { BaseSpace } from 'app/components/common/BaseSpace'
import { BaseForm } from 'app/components/common/forms/BaseForm'
import { BaseInput } from 'app/components/common/inputs/BaseInput'
import {
  BaseModalComponent,
  IRefModal,
} from 'app/components/common/ModalComponent'
import { BaseDatePicker } from 'app/components/common/pickers/BaseDatePicker'
import { IFormUpdateEvent } from 'app/containers/EventManagement/layouts/UpdateEvent/type'
import { EventStatusEnum } from 'app/containers/EventManagement/type'
import { notificationController } from 'app/controllers/notification-controller'
import { useVerifyPermission } from 'app/helpers/permission.helper'
import {
  disabledEndDate,
  disabledStartDate,
  disabledTimeEnd,
  disabledTimeStart,
} from 'app/hook'
import { useUpdateEvent } from 'app/react-query/hook/event'
import dayjs, { Dayjs } from 'dayjs'
import { isEqual } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

export const ModalUpdate = ({ event }: { event: IEvent }) => {
  const { t } = useTranslation()
  const modalRef = useRef<IRefModal>({})
  const [form] = BaseForm.useForm()
  const [eventType, setEventType] = useState<TypeEventEnum | undefined>(
    undefined,
  )
  const [formInfo, setFormInfo] = useState<IFormUpdateEvent>({})

  const { mutateAsync: mutateAsyncUpdateEvent, isLoading: isLoadingSubmit } =
    useUpdateEvent()

  const { verifyPermission } = useVerifyPermission()

  const handleSubmit = async (formPayload: IFormUpdateEvent) => {
    if (!event?.id) return

    const payload: IPayloadUpdateEvent = {
      id: event?.id,
      name_event: formPayload?.name,
      time_end: formPayload?.endDate
        ? formPayload?.endDate?.toISOString()
        : new Date('9999-12-31').toISOString(),
      time_start: formPayload?.startDate
        ? formPayload?.startDate?.toISOString()
        : new Date('2000-01-01').toISOString(),
      description: formPayload?.description ?? '-',
      time_register_end: formPayload?.endDateRegister?.toISOString(),
      time_register_start: formPayload?.startDateRegister?.toISOString(),
      type: eventType ?? event?.type,
      link: formPayload?.link,
      documents: [],
      addresses: [],
      time_create_event: new Date().toISOString(),
      isHaveTeam: event?.isHaveTeam,
    }
    // action date
    const res = await mutateAsyncUpdateEvent(payload)

    if (res) {
      notificationController.success({ message: 'Thành công!' })
      modalRef.current?.hide?.()
      window.location.reload()
    }
  }

  useEffect(() => {
    if (!event) {
      notificationController?.error?.({
        message: 'Không tìm thấy sự kiện',
      })
      modalRef?.current?.hide?.()
      return
    }

    if (event) {
      form?.setFieldsValue({
        name: event?.name_event,
        description: event?.description,
        startDate: event?.time_start ? dayjs(event?.time_start) : undefined,
        endDate: event?.time_end ? dayjs(event?.time_end) : undefined,
        startDateRegister: event?.time_register_start
          ? dayjs(event?.time_register_start)
          : undefined,
        endDateRegister: event?.time_register_end
          ? dayjs(event?.time_register_end)
          : undefined,

        link: event?.link,
        type: event?.type,
        isHaveTeam: event?.isHaveTeam,
      })

      setEventType(event?.type)
    }
  }, [event])

  return (
    <BaseModalComponent
      titleModal={'Cập nhật thông tin sự kiện'}
      titleOpenModal={t(R.strings.update)}
      ref={modalRef}
      handleSubmit={form?.submit}
      isLoadingConfirm={isLoadingSubmit}
      buttonOpenModal={
        <BaseButton
          icon={<EditOutlined rev={undefined} />}
          onClick={() => modalRef?.current?.open?.()}
          type='text'
        />
      }
      renderContent={
        <BaseForm form={form} onFinish={handleSubmit}>
          <RootWrapper gutter={[16, 8]} align={'top'}>
            <BaseCol xl={24}>
              <BaseSpace size={0}>
                <BaseForm.Item
                  name={'name'}
                  required
                  label={t(R.strings.event_name)}
                  rules={[
                    {
                      required: true,
                      message: t(R.strings.require_field, {
                        field: t(R.strings.event_name),
                      }),
                    },
                  ]}
                >
                  <BaseInput placeholder={t(R.strings.enter_event_name)} />
                </BaseForm.Item>

                {isEqual(eventType, TypeEventEnum?.NEWS) ? null : (
                  <BaseRow align={'top'} gutter={[16, 8]}>
                    <BaseCol xl={12}>
                      <BaseForm.Item
                        name={'startDateRegister'}
                        required
                        label={t(R.strings.start_date_register)}
                        dependencies={['endDateRegister']}
                        rules={[
                          {
                            required: true,
                            message: t(R.strings.require_field, {
                              field: t(R.strings.start_date_register),
                            }),
                          },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const day = value as Dayjs

                              if (!day || !getFieldValue('endDateRegister')) {
                                return Promise.resolve()
                              }
                              if (
                                day.isSame(getFieldValue('endDateRegister')) ||
                                day.isBefore(getFieldValue('endDateRegister'))
                              ) {
                                return Promise.resolve()
                              }

                              return Promise.reject(
                                t(R.strings.start_date_must_be_before_end_date),
                              )
                            },
                          }),
                        ]}
                      >
                        <BaseDatePickerStyled
                          format={'DD-MM-YYYY HH:mm'}
                          placeholder={t(R.strings.start_date_register)}
                          showTime={{ defaultValue: dayjs('00:00', 'HH:mm') }}
                          disabled={
                            isEqual(event?.status, EventStatusEnum.FINISHED) ||
                            isEqual(
                              event?.status,
                              EventStatusEnum.IN_PROGRESS,
                            ) ||
                            isEqual(event?.status, EventStatusEnum.REGISTRATION)
                          }
                          onChange={value =>
                            setFormInfo?.({
                              ...formInfo,
                              startDateRegister: value,
                            })
                          }
                          disabledDate={e =>
                            disabledStartDate(e, formInfo?.endDateRegister)
                          }
                          disabledTime={e =>
                            disabledTimeStart(e, formInfo?.endDateRegister)
                          }
                        />
                      </BaseForm.Item>
                    </BaseCol>
                    <BaseCol xl={12}>
                      <BaseForm.Item
                        name={'endDateRegister'}
                        required
                        label={t(R.strings.end_date_register)}
                        dependencies={['startDateRegister', 'startDate']}
                        rules={[
                          {
                            required: true,
                            message: t(R.strings.require_field, {
                              field: t(R.strings.end_date_register),
                            }),
                          },

                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const day = value as Dayjs

                              if (!day || !getFieldValue('startDateRegister')) {
                                return Promise.resolve()
                              }
                              if (
                                day.isSame(
                                  getFieldValue('startDateRegister'),
                                ) ||
                                day.isAfter(getFieldValue('startDateRegister'))
                              ) {
                                return Promise.resolve()
                              }

                              return Promise.reject(
                                t(R.strings.end_date_must_be_after_start_date),
                              )
                            },
                          }),

                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const day = value as Dayjs

                              if (!day || !getFieldValue('startDate')) {
                                return Promise.resolve()
                              }
                              if (day.isBefore(getFieldValue('startDate'))) {
                                return Promise.resolve()
                              }

                              return Promise.reject(
                                t(
                                  R.strings
                                    .end_date_register_must_be_before_start_date,
                                ),
                              )
                            },
                          }),
                        ]}
                      >
                        <BaseDatePickerStyled
                          format={'DD-MM-YYYY HH:mm'}
                          placeholder={t(R.strings.end_date_register)}
                          showTime={{ defaultValue: dayjs('23:59', 'HH:mm') }}
                          disabled={
                            isEqual(event?.status, EventStatusEnum.FINISHED) ||
                            isEqual(event?.status, EventStatusEnum.IN_PROGRESS)
                          }
                          onChange={value =>
                            setFormInfo?.({
                              ...formInfo,
                              endDateRegister: value,
                            })
                          }
                          disabledDate={e =>
                            disabledEndDate(e, formInfo?.startDateRegister)
                          }
                          disabledTime={e =>
                            disabledTimeEnd(e, formInfo?.startDateRegister)
                          }
                        />
                      </BaseForm.Item>
                    </BaseCol>
                  </BaseRow>
                )}

                <BaseRow align={'top'} gutter={[16, 8]}>
                  <BaseCol xl={12}>
                    <BaseForm.Item
                      name={'startDate'}
                      required
                      label={t(R.strings.start_date_event)}
                      dependencies={['endDate', 'endDateRegister']}
                      rules={[
                        {
                          required: true,
                          message: t(R.strings.require_field, {
                            field: t(R.strings.start_date_event),
                          }),
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const day = value as Dayjs

                            if (!day || !getFieldValue('endDate')) {
                              return Promise.resolve()
                            }
                            if (
                              day.isSame(getFieldValue('endDate')) ||
                              day.isBefore(getFieldValue('endDate'))
                            ) {
                              return Promise.resolve()
                            }

                            return Promise.reject(
                              t(R.strings.start_date_must_be_before_end_date),
                            )
                          },
                        }),

                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const day = value as Dayjs

                            if (!day || !getFieldValue('endDateRegister')) {
                              return Promise.resolve()
                            }
                            if (day.isAfter(getFieldValue('endDateRegister'))) {
                              return Promise.resolve()
                            }

                            return Promise.reject(
                              t(
                                R.strings
                                  .start_date_event_must_be_after_end_date_register,
                              ),
                            )
                          },
                        }),
                      ]}
                    >
                      <BaseDatePickerStyled
                        format={'DD-MM-YYYY HH:mm'}
                        placeholder={t(R.strings.start_date_event)}
                        showTime={{ defaultValue: dayjs('00:00', 'HH:mm') }}
                        disabled={(() => {
                          if (
                            verifyPermission(PermissionEnum.UpdateEndTimeEvent)
                          ) {
                            return false
                          }
                          return (
                            isEqual(event?.status, EventStatusEnum.FINISHED) ||
                            isEqual(event?.status, EventStatusEnum.IN_PROGRESS)
                          )
                        })()}
                        onChange={value =>
                          setFormInfo?.({
                            ...formInfo,
                            startDate: value,
                          })
                        }
                        disabledDate={e =>
                          disabledStartDate(e, formInfo?.endDate)
                        }
                        disabledTime={e =>
                          disabledTimeStart(e, formInfo?.endDate)
                        }
                      />
                    </BaseForm.Item>
                  </BaseCol>

                  <BaseCol xl={12}>
                    <BaseForm.Item
                      name={'endDate'}
                      required
                      label={t(R.strings.end_date_event)}
                      dependencies={['startDate']}
                      rules={[
                        {
                          required: true,
                          message: t(R.strings.require_field, {
                            field: t(R.strings.end_date_event),
                          }),
                        },

                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const day = value as Dayjs

                            if (!day || !getFieldValue('startDate')) {
                              return Promise.resolve()
                            }
                            if (
                              day.isSame(getFieldValue('startDate')) ||
                              day.isAfter(getFieldValue('startDate'))
                            ) {
                              return Promise.resolve()
                            }

                            return Promise.reject(
                              t(R.strings.end_date_must_be_after_start_date),
                            )
                          },
                        }),
                      ]}
                    >
                      <BaseDatePickerStyled
                        format={'DD-MM-YYYY HH:mm'}
                        placeholder={t(R.strings.end_date_event)}
                        showTime={{ defaultValue: dayjs('23:59', 'HH:mm') }}
                        disabled={(() => {
                          if (
                            isEqual(event?.status, EventStatusEnum.FINISHED)
                          ) {
                            if (
                              verifyPermission(
                                PermissionEnum.UpdateEndTimeEvent,
                              )
                            ) {
                              return false
                            }
                            return true
                          }
                        })()}
                        onChange={value =>
                          setFormInfo?.({
                            ...formInfo,
                            endDate: value,
                          })
                        }
                        disabledDate={e =>
                          disabledEndDate(e, formInfo?.startDate)
                        }
                        disabledTime={e =>
                          disabledTimeEnd(e, formInfo?.startDate)
                        }
                      />
                    </BaseForm.Item>
                  </BaseCol>
                </BaseRow>

                <BaseForm.Item
                  name={'description'}
                  label={t(R.strings.description)}
                >
                  <BaseInput.TextArea
                    placeholder={t(R.strings.description)}
                    autoSize={{ minRows: 3, maxRows: 20 }}
                  />
                </BaseForm.Item>

                {isEqual(eventType, TypeEventEnum?.NEWS) ? (
                  <BaseForm.Item
                    required
                    rules={[
                      {
                        required: true,
                        message: t(R.strings.require_field, {
                          field: 'Link tin tức',
                        }),
                      },
                    ]}
                    name={'link'}
                    label={'Link tin tức'}
                  >
                    <BaseInput placeholder="Nhập link tin tức" />
                  </BaseForm.Item>
                ) : null}
              </BaseSpace>
            </BaseCol>
          </RootWrapper>
        </BaseForm>
      }
    />
  )
}

const RootWrapper = styled(BaseRow)`
  max-height: 500px;
  overflow-y: auto;
`

const BaseDatePickerStyled = styled(BaseDatePicker)`
  width: 100%;
`
